import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Search, MapPin, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { apiError } from '../../utils/apiError';
import { courseData } from '../skins/courseData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CourseLookupModal({ isOpen, onClose, onCourseSelect, currentCourseName = '', title = 'Find Your Course', closeOnSelect = true, returnFocusId }) {
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState([]);
  const [results, setResults] = useState([]);
  const [course, setCourse] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [nines, setNines] = useState(['', '']);
  const requestRef = useRef(null);
  const active = useRef(false);
  const savingRef = useRef(false);
  const scrollRef = useRef(null);
  const initialName = useRef(currentCourseName);
  initialName.current = currentCourseName;
  useEffect(() => {
    active.current = isOpen;
    if (!isOpen) return;
    setQuery(initialName.current || ''); setCourse(null); setResults([]); setError(''); setBusy(false); setSaving(false); setNines(['', '']);
    const controller = new AbortController();
    axios.get(`${API}/courses`, { signal: controller.signal, timeout: 15000 }).then(({ data }) => {
      setRecent(Array.isArray(data) ? data.filter(c => c?.course_id && c.name).slice(0, 5) : []);
    }).catch(() => setRecent([]));
    return () => { active.current = false; controller.abort(); requestRef.current?.abort(); };
  }, [isOpen]); // Opening a dialog seeds the query; background event refreshes must not reset it.
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; }, [course, busy]);
  const request = async action => {
    requestRef.current?.abort();
    const controller = new AbortController(); requestRef.current = controller;
    setBusy(true); setError('');
    try {
      const { data } = await action({ signal: controller.signal, timeout: 25000 });
      if (controller.signal.aborted || !active.current) return;
      if (!data.success) throw new Error(data.message || data.error || 'No course information returned. Try another search.');
      if (data.course?.name) { setCourse(data.course); setNines(['', '']); }
      else if (Array.isArray(data.courses) && data.courses.length) setResults(data.courses.filter(c => c?.id != null));
      else throw new Error('No matching courses found. Try another name.');
    } catch (err) {
      if (!controller.signal.aborted && active.current) setError(apiError(err, err.message || 'Course lookup failed. Please retry.'));
    } finally { if (!controller.signal.aborted && active.current) setBusy(false); }
  };
  const search = () => {
    if (!query.trim() || busy) return;
    setResults([]); setCourse(null);
    request(config => axios.post(`${API}/voice/lookup-course`, { course_name: query.trim() }, config));
  };
  const selectResult = id => request(config => axios.get(`${API}/courses/golfapi/${encodeURIComponent(id)}`, config));
  const selectSaved = saved => {
    if (saved.api_course_id) selectResult(saved.api_course_id);
    else { setCourse(saved); setError(''); }
  };
  const searchAgain = () => { setCourse(null); setError(''); };
  const hasHoleData = (Array.isArray(course?.hole_pars) && course.hole_pars.length >= 9) ||
    (course?.nines?.length >= 2 && nines[0] !== nines[1] && nines.every(name => {
      const nine = course.nines.find(item => item.name === name);
      return (nine?.pars || nine?.hole_pars || []).length === 9;
    }));
  const confirm = async () => {
    if (!course || !hasHoleData || savingRef.current) return;
    savingRef.current = true; setSaving(true); setError('');
    try {
      let selected = courseData(course);
      if (course.nines?.length >= 2) {
        const first = course.nines.find(n => n.name === nines[0]);
        const second = course.nines.find(n => n.name === nines[1]);
        if (!first || !second || first === second) throw new Error('Choose two different nines.');
        selected = { ...selected, course_id: null, api_course_id: null, name: `${course.name} (${first.name}/${second.name})`,
          hole_pars: [...(first.pars || first.hole_pars || []), ...(second.pars || second.hole_pars || [])],
          hole_handicaps: [...(first.hole_handicaps || []), ...(second.hole_handicaps || [])], tees: [], hole_yardages: [], holes: 18 };
        selected.par = selected.hole_pars.reduce((a, b) => a + b, 0);
      }
      if (!selected.course_id) {
        const { data } = await axios.post(`${API}/courses/create-from-lookup`, selected, { timeout: 20000 });
        if (!data.course_id) throw new Error('The course could not be saved. Please retry.');
        selected = { ...selected, ...data };
      }
      if (!active.current) return;
      await onCourseSelect(selected);
      if (active.current && closeOnSelect) onClose();
    } catch (err) { if (active.current) setError(apiError(err, err.message || 'Unable to save course. Please retry.')); }
    finally { savingRef.current = false; if (active.current) setSaving(false); }
  };
  return <Dialog open={isOpen} onOpenChange={open => { if (!open && !saving) onClose(); }}>
    <DialogContent hideClose={saving} aria-describedby={undefined} data-testid="course-lookup-dialog"
      overlayClassName="z-[80]" className="ps-themed-dialog z-[81] grid max-w-md w-[calc(100%_-_2rem)] h-[min(85dvh,680px)] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden"
      onPointerDownOutside={event => event.preventDefault()}
      onInteractOutside={event => event.preventDefault()}
      onEscapeKeyDown={event => { event.stopPropagation(); if (savingRef.current) event.preventDefault(); }}
      onCloseAutoFocus={event => {
        const target = returnFocusId && document.getElementById(returnFocusId);
        if (target) { event.preventDefault(); target.focus({ preventScroll: true }); }
      }}>
      <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
      <div ref={scrollRef} data-testid="course-lookup-scroll" className="space-y-4 min-h-0 overflow-y-auto overscroll-contain pr-1">
        {error && <p role="alert" data-testid="course-lookup-error" className="text-sm text-red-600">{error}</p>}
        {!course && <>
          {!busy && recent.length > 0 && <div className="flex flex-wrap gap-2" data-testid="course-lookup-saved">
            <p className="w-full text-sm">Your saved courses</p>
            {recent.map(c => <Button type="button" variant="outline" className="max-w-full h-auto whitespace-normal text-left" data-testid={`course-quick-pick-${c.course_id}`} key={c.course_id} onClick={() => selectSaved(c)}>{c.name}</Button>)}
          </div>}
          <label className="block text-sm" htmlFor="course-lookup-name">Course name</label>
          <Input id="course-lookup-name" data-testid="course-lookup-name" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') search(); }} disabled={busy} placeholder="Course or club name" />
          {busy && <p role="status" data-testid="course-lookup-loading">Fetching course information…</p>}
          {!busy && results.map(c => <button type="button" key={c.id} data-testid={`course-result-${c.id}`} className="w-full text-left border p-3 rounded-lg break-words" onClick={() => selectResult(c.id)}>
            <span className="font-medium block">{c.club_name || c.course_name}</span>
            <span className="text-sm block">{c.course_name}</span><span className="text-xs">{typeof c.location === 'string' ? c.location : c.location?.address}</span>
          </button>)}
        </>}
        {course && <>
          <div data-testid="course-lookup-details" className="space-y-2 break-words">
            <h2 className="font-bold">{course.name}</h2>
            {course.location && <p className="text-sm flex gap-2"><MapPin className="w-4 h-4 shrink-0" />{typeof course.location === 'string' ? course.location : course.location.address}</p>}
            <p data-testid="course-lookup-par">{course.holes || course.hole_pars?.length || 18} holes · Par {course.par ?? 'not available'}</p>
            <p className="text-xs" data-testid="course-lookup-source">{course.notes || (course.course_id ? 'Saved course' : 'GolfCourseAPI course data')}</p>
            <div className="flex flex-wrap gap-1" data-testid="course-lookup-pars">{(course.hole_pars || []).map((par, i) => <span key={i} className="border rounded px-2 py-1 text-sm">{par ?? '—'}</span>)}</div>
            {(!hasHoleData || !course.tees?.length) && <p role="status" data-testid="course-lookup-incomplete">{!hasHoleData ? 'Hole data is missing.' : 'Tee data is missing.'} Search again for updated course details.</p>}
            <ul className="space-y-1 text-sm" data-testid="course-lookup-tees">{(course.tees || []).map((tee, i) => <li key={i}>{tee.name}: {tee.total_yards ?? '—'} yards</li>)}</ul>
          </div>
          {course.nines?.length >= 2 && nines.map((value, index) => <select key={index} data-testid={`course-nine-${index}`} aria-label={index ? 'Second nine' : 'First nine'} className="w-full border rounded p-2" value={value} onChange={e => setNines(prev => prev.map((v, i) => i === index ? e.target.value : v))}>
            <option value="">{index ? 'Second nine' : 'First nine'}</option>{course.nines.map(n => <option key={n.name} value={n.name}>{n.name}</option>)}
          </select>)}
        </>}
      </div>
      <div data-testid="course-lookup-actions" className="grid grid-cols-1 sm:grid-cols-2 gap-2 border-t pt-3 min-h-[88px] sm:min-h-[49px] content-end">
        {course ? <>
          <Button type="button" variant="outline" data-testid="course-lookup-back" disabled={saving} onClick={searchAgain}>Search again</Button>
          <Button type="button" data-testid="course-lookup-confirm" disabled={saving || !hasHoleData} onClick={confirm} className="flex-1 min-w-0 bg-green-700 text-white hover:bg-green-800"><Check className="w-4 h-4 mr-1" />{saving ? 'Saving…' : 'Use This Course'}</Button>
        </> : <Button type="button" className="w-full col-span-full bg-green-700 text-white hover:bg-green-800" data-testid="course-lookup-search" disabled={busy || !query.trim()} onClick={search}><Search className="w-4 h-4 mr-2" />{busy ? 'Looking up course…' : 'Find My Course'}</Button>}
      </div>
    </DialogContent>
  </Dialog>;
}